import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  sniperOrchestratorAxiosApi,
  notificationsAxiosApi,
} from 'src/Config/axiosApi';
// P1: the control-plane /match-events/* routes moved from prediction-manager to
// sniper-orchestrator (with the live-match-events layer). The durable history +
// state backfill (/match-events/history, /state) stay on notifications-websocket.
import type {
  ActivatableMatch,
  ActiveMatch,
  MatchEventFrame,
  UpsertMatchDto,
} from '../types/matchEvents.types';
import type { MatchStatePayload } from '../types/sniper.types';

/**
 * Control-plane REST for the Live Match Events tab (frozen contract §REST, base
 * path `/match-events` — P1: served by the sniper-orchestrator service). Re-uses
 * the shared `sniperOrchestratorAxiosApi` (Bearer + 401-refresh interceptors).
 * Mirrors `useGroups.ts` — a GET list query + activate/deactivate/upsert
 * mutations that invalidate the list. The durable history + state backfill stay
 * on notifications-websocket (`notificationsAxiosApi`).
 */

const MATCHES_KEY = 'match-events-matches';
const ACTIVE_KEY = 'match-events-active';

/** react-query base key for the per-fixture backend `match_state` backfill. */
export const MATCH_EVENT_STATE_KEY = 'match-events-state';

/** The `match_state` backfill query key for one fixture (single source of truth). */
export function matchEventStateQueryKey(fixtureId: number | null) {
  return [MATCH_EVENT_STATE_KEY, fixtureId] as const;
}

/** Invalidate BOTH the activatable-match list and the live-hub active list. */
function invalidateMatchLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries([MATCHES_KEY]);
  qc.invalidateQueries([ACTIVE_KEY]);
}

/**
 * `GET /match-events/active` (prediction-manager) — the live-hub feed: every
 * ACTIVE match with its `source` provenance (group / manual / both) and the
 * runtime `live` health flag. Polled so a group auto-adding a match, or the
 * feed going live, surfaces without a manual refresh.
 */
export function useActiveMatches() {
  return useQuery<ActiveMatch[]>(
    [ACTIVE_KEY],
    async () => {
      const { data } = await sniperOrchestratorAxiosApi.get<
        ActiveMatch[] | { matches: ActiveMatch[] }
      >('/match-events/active');
      return Array.isArray(data) ? data : (data?.matches ?? []);
    },
    { refetchInterval: 15_000, keepPreviousData: true },
  );
}

/**
 * `GET /match-events/history/:fixtureId` (notifications-websocket) — the durable
 * match_events history (24h TTL). Loaded when a fixture is selected so a page
 * refresh rebuilds the FULL feed instead of only the ~20-min live replay.
 */
export function useMatchEventsHistory(fixtureId: number | null) {
  return useQuery<MatchEventFrame[]>(
    ['match-events-history', fixtureId],
    async () => {
      const { data } = await notificationsAxiosApi.get<MatchEventFrame[]>(
        `/match-events/history/${fixtureId}`,
      );
      return Array.isArray(data) ? data : [];
    },
    {
      enabled: fixtureId != null,
      staleTime: 15_000,
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * The `GET /match-events/state/:fixtureId` backfill payload (notifications-
 * websocket). The backend derives the authoritative `MatchState`; `null` when
 * it has not produced one yet for that fixture. IDENTICAL to the WS `match_state`
 * envelope — both alias the single `MatchStatePayload` contract type (no drift).
 */
export type MatchEventStateResponse = MatchStatePayload;

/**
 * Fetch the backend-authoritative `MatchState` backfill for a fixture. Split out
 * from the hook so the URL + null-coalescing is unit-testable without React.
 */
export async function fetchMatchEventState(
  fixtureId: number,
): Promise<MatchEventStateResponse | null> {
  const { data } = await notificationsAxiosApi.get<MatchEventStateResponse | null>(
    `/match-events/state/${fixtureId}`,
  );
  return data ?? null;
}

/**
 * `GET /match-events/state/:fixtureId` (notifications-websocket) — the backend's
 * last-known `MatchState` for a fixture. Used for the INITIAL render before the
 * first live `match_state` WS push arrives; the live value then supersedes it.
 * The FE renders this state — it never re-derives score/corners from incidents.
 */
export function useMatchEventState(fixtureId: number | null) {
  return useQuery<MatchEventStateResponse | null>(
    matchEventStateQueryKey(fixtureId),
    () => fetchMatchEventState(fixtureId as number),
    {
      enabled: fixtureId != null,
      staleTime: 15_000,
      refetchOnWindowFocus: false,
    },
  );
}

/**
 * One play-period's EXACT score/corners in a seed. `period` is the LSports play
 * period id — `10` = 1st half, `20` = 2nd half. When a seed carries `periods`, the
 * engine seeds those ledger buckets directly, so there is no absorbing anchor and a
 * goal scored right after the seed still counts (a global-only seed can't know the
 * current period's split — that's what once froze a header at 0–0 after a goal).
 */
export interface SeedPeriodDto {
  period: number;
  home: number;
  away: number;
  homeCorners?: number;
  awayCorners?: number;
}

/**
 * Body for `POST /match-events/matches/:fixtureId/seed-score`. Establishes (or
 * corrects) the backend MatchState baseline when the LSports feed cold-started
 * mid-match and no authoritative score exists yet. Corners optional. All values
 * are non-negative integers (validated server-side by the DTO).
 *
 * `homeScore`/`awayScore` are the MATCH TOTAL. `periods` is an OPTIONAL exact
 * per-half breakdown: when present the total equals its sum and the engine seeds
 * the per-period ledger exactly (no absorption).
 */
export interface SeedScoreDto {
  homeScore: number;
  awayScore: number;
  homeCorners?: number;
  awayCorners?: number;
  periods?: SeedPeriodDto[];
}

/**
 * `POST /match-events/matches/:fixtureId/seed-score` (prediction-manager). The
 * operator manually seeds the current score/corners so the engine can establish
 * the live-match baseline (the LSports Incidents feed has no replay/backfill on a
 * mid-match cold-start). The control-plane only writes the `match-events:seed:*`
 * Redis key + publishes the change hint — it NEVER writes `match_state`. The
 * engine interprets the seed and re-publishes the authoritative MatchState over
 * the `match_state` WS event, so the FE renders the seeded board from backend
 * state (never the typed values directly). Override allowed (correct an existing
 * baseline). Failures surface through the global mutation-error toast via
 * `mutationKey`; on success we also nudge the REST backfill so a fixture with no
 * live socket value repaints immediately.
 */
export function useSeedMatchScore() {
  const qc = useQueryClient();
  return useMutation(
    async ({ fixtureId, dto }: { fixtureId: number; dto: SeedScoreDto }) => {
      const { data } = await sniperOrchestratorAxiosApi.post(
        `/match-events/matches/${fixtureId}/seed-score`,
        dto,
      );
      return data;
    },
    {
      mutationKey: 'match-events:seed-score',
      onSuccess: (_data, { fixtureId }) => {
        qc.invalidateQueries(matchEventStateQueryKey(fixtureId));
      },
    },
  );
}

/** `GET /match-events/matches` — the activatable-match list (+ `active`). */
export function useMatchEventsMatches() {
  return useQuery<ActivatableMatch[]>(
    [MATCHES_KEY],
    async () => {
      const { data } = await sniperOrchestratorAxiosApi.get<
        ActivatableMatch[] | { matches: ActivatableMatch[] }
      >('/match-events/matches');
      // Canonical wire shape is a bare array; the `{ matches }` branch is
      // defensive against an enveloped payload.
      return Array.isArray(data) ? data : (data?.matches ?? []);
    },
    { refetchInterval: 15_000, keepPreviousData: true },
  );
}

/** `POST /match-events/matches` — upsert on `fixtureId`. */
export function useUpsertMatch() {
  const qc = useQueryClient();
  return useMutation(
    async (dto: UpsertMatchDto) => {
      const { data } = await sniperOrchestratorAxiosApi.post<ActivatableMatch>(
        '/match-events/matches',
        dto,
      );
      return data;
    },
    {
      mutationKey: 'match-events:upsert',
      onSuccess: () => {
        invalidateMatchLists(qc);
      },
    },
  );
}

/** `POST /match-events/matches/:fixtureId/activate`. */
export function useActivateMatch() {
  const qc = useQueryClient();
  return useMutation(
    async (fixtureId: number) => {
      const { data } = await sniperOrchestratorAxiosApi.post(
        `/match-events/matches/${fixtureId}/activate`,
      );
      return data;
    },
    {
      mutationKey: 'match-events:activate',
      onSuccess: () => {
        invalidateMatchLists(qc);
      },
    },
  );
}

/** `POST /match-events/matches/:fixtureId/deactivate`. */
export function useDeactivateMatch() {
  const qc = useQueryClient();
  return useMutation(
    async (fixtureId: number) => {
      const { data } = await sniperOrchestratorAxiosApi.post(
        `/match-events/matches/${fixtureId}/deactivate`,
      );
      return data;
    },
    {
      mutationKey: 'match-events:deactivate',
      onSuccess: () => {
        invalidateMatchLists(qc);
      },
    },
  );
}

/** `DELETE /match-events/matches/:fixtureId` — deactivate + remove doc. */
export function useDeleteMatch() {
  const qc = useQueryClient();
  return useMutation(
    async (fixtureId: number) => {
      const { data } = await sniperOrchestratorAxiosApi.delete(
        `/match-events/matches/${fixtureId}`,
      );
      return data;
    },
    {
      mutationKey: 'match-events:delete',
      onSuccess: () => {
        invalidateMatchLists(qc);
      },
    },
  );
}
